import { useState, useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import { AiErrorInfo } from '@/core/api/types';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Progress } from '@/shared/components/ui/progress';
import {
  MapPin, Star, Lightbulb, ChevronDown, Phone, Mail, Loader2,
} from 'lucide-react';
import { JourneyProgress } from './JourneyProgress';
import { ProviderMatchingSkeleton } from '@/features/resource-hub/components/ProviderMatchingSkeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';

const STICKY_OFFSET = 160;

type ApiProvider = {
  provider_id: string;
  provider_name: string | null;
  overview: string | null;
  contact_information: string | null;
  experience: string | null;
  location: string | null;
  rating: number | null;
  consistent: null;
  availability: null;
  email: string | null;
  credentials: string[] | null;
  specialties: string[] | null;
  acceptingNewPatients: null;
  provider_category_name: string | null;
};

type ApiSpecialty = {
  specialty_id: string;
  name: string;
  what_they_do: string | null;
  why_you_need_them: string | null;
  providers: ApiProvider[];
};

type Provider = {
  id: string;
  name: string;
  specialty: string;
  provider_category_name?: string;
  location: string;
  rating: number | null;
  bio: string;
  experience?: string;
  phone?: string;
  email?: string;
  credentials?: string[];
  specialties?: string[];
};

type ProviderCategory = {
  specialty_id: string;
  title: string;
  description: string | null;
  whatTheyDo: string | null;
  providers: Provider[];
};

type ProviderMatchingState = {
  sessionId?: string;
};

export function ProviderMatching() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = (location.state as ProviderMatchingState | null)?.sessionId;
  const { categoryIndex } = useParams();

  const currentIndex = Number.isFinite(Number(categoryIndex)) ? Number(categoryIndex) : 0;
  const selectionsKey = sessionId ? `fp-selections-${sessionId}` : null;
  const [selectedProviders, setSelectedProviders] = useState<Map<string, Set<string>>>(() => {
    if (!selectionsKey) return new Map();
    try {
      const saved = localStorage.getItem(selectionsKey);
      if (!saved) return new Map();
      const parsed = JSON.parse(saved) as [string, string[]][];
      return new Map(parsed.map(([k, v]) => [k, new Set(v)]));
    } catch {
      return new Map();
    }
  });

  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [expandedBios, setExpandedBios] = useState<Set<string>>(new Set());

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentIndex]);
  const [isBuildingPlan, setIsBuildingPlan] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isAllDone, setIsAllDone] = useState(false);
  // How many specialties Phase 2 has finished so far. Backend processes specialties
  // strictly in order (1st, then 2nd, then 3rd...), so this count tells us exactly
  // how many leading entries in `categories` are done — used instead of filtering by
  // providers.length > 0, which broke order whenever an earlier specialty legitimately
  // had zero matches (NO_RESULTS) and a later one finished with results first.
  const [completedCount, setCompletedCount] = useState(0);

  const { data: specialtyTypes = [] } = useQuery<{ specialty_id: string; name: string }[]>({
    queryKey: ['find-providers-specialty-types', sessionId],
    enabled: !!sessionId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data } = await apiClient.get<{ specialty_id: string; name: string }[]>(
        '/find-providers/specialty-types',
        { params: { session_id: sessionId } },
      );
      return data;
    },
  });

  const isReadyRef = useRef(false);

  const { data: categories = [], isPending: isLoadingCategories, refetch } = useQuery<ProviderCategory[]>({
    queryKey: ['find-providers-specialties', sessionId],
    enabled: !!sessionId && isReady,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSpecialty[]>('/find-providers/specialties', {
        params: { session_id: sessionId },
      });
      return data.map((specialty) => ({
        specialty_id: specialty.specialty_id,
        title: specialty.name,
        description: specialty.why_you_need_them ?? '',
        whatTheyDo: specialty.what_they_do ?? '',
        providers: specialty.providers.map((p) => ({
          id: p.provider_id,
          name: p.provider_name ?? '',
          specialty: specialty.name,
          provider_category_name: p.provider_category_name ?? undefined,
          location: p.location ?? '',
          rating: p.rating ?? null,
          bio: p.overview ?? '',
          phone: p.contact_information ?? undefined,
          experience: p.experience ?? undefined,
          email: p.email ?? undefined,
          credentials: p.credentials ?? undefined,
          specialties: p.specialties ?? undefined,
        })),
      }));
    },
  });

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!sessionId) return;
    let sseSource: EventSource | null = null;

    const openSse = () => {
      const base = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api/v1';
      const url = `${base}/find-providers/stream?session_id=${encodeURIComponent(sessionId)}`;
      sseSource = new EventSource(url);
      sseSource.onmessage = (e: MessageEvent) => {
        const msg = JSON.parse(e.data as string) as { status: string };
        if (msg.status === 'SPECIALTY_READY') {
          setCompletedCount((c) => c + 1);
          if (!isReadyRef.current) {
            isReadyRef.current = true;
            setIsReady(true);
          } else {
            void refetchRef.current();
          }
        } else if (msg.status === 'READY') {
          setIsAllDone(true);
          if (!isReadyRef.current) {
            isReadyRef.current = true;
            setIsReady(true);
          } else {
            void refetchRef.current();
          }
          sseSource?.close();
        }
      };
      sseSource.onerror = () => {
        if (!isReadyRef.current) {
          isReadyRef.current = true;
          setIsReady(true);
        }
        sseSource?.close();
      };
    };

    void apiClient
      .get<{ status: string }>('/find-providers/status', { params: { session_id: sessionId } })
      .then(({ data }) => {
        if (data.status === 'READY') {
          isReadyRef.current = true;
          setIsAllDone(true);
          setIsReady(true);
        } else {
          openSse();
        }
      })
      .catch(() => openSse());

    return () => sseSource?.close();
  }, [sessionId]);

  useEffect(() => {
    if (!selectionsKey) return;
    const toSave = [...selectedProviders.entries()].map(([k, v]) => [k, [...v]]);
    localStorage.setItem(selectionsKey, JSON.stringify(toSave));
  }, [selectedProviders, selectionsKey]);

  if (!sessionId) {
    return <Navigate to="/redesign-chatbot/transition" replace />;
  }
    // During pipeline: show specialties strictly in backend processing order — the first
  // `completedCount` entries in `categories`, since Phase 2 finishes them sequentially.
  // After all done: show all specialties including those with no providers.
  const visibleCategories = isAllDone
    ? categories
    : categories.slice(0, completedCount);

  const category = visibleCategories[currentIndex];
  // Use the full specialty-type count when available so the header reflects the
  // true total (e.g. "1 of 4") instead of however many have loaded providers so far.
  const totalProviderTypes = Math.max(specialtyTypes.length, visibleCategories.length);
  const progress = ((currentIndex + 1) / Math.max(totalProviderTypes, 1)) * 100;
  // The next specialty hasn't loaded yet and the pipeline isn't done — don't let the
  // user advance past it or prematurely show "Build My Care Plan" on a false last step.
  const isWaitingForNext = currentIndex >= visibleCategories.length - 1 && !isAllDone;
  const isLastStep = isAllDone && visibleCategories.length > 0 && currentIndex === visibleCategories.length - 1;

  const isSelected = (providerId: string) =>
    selectedProviders.get(category?.specialty_id ?? '')?.has(providerId) ?? false;

  const handleSelectProvider = (providerId: string) => {
    const specialtyId = category.specialty_id;
    setSelectedProviders((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(specialtyId) ?? []);
      if (set.has(providerId)) {
        set.delete(providerId);
      } else {
        set.add(providerId);
      }
      next.set(specialtyId, set);
      return next;
    });
  };

  const buildCarePlanWithSelections = async (selections: Map<string, Set<string>>) => {
    setIsBuildingPlan(true);
    const selectedList: { specialty_id: string; provider_ids: string[] }[] = [];
    for (const [specialtyId, providerIds] of selections.entries()) {
      if (providerIds.size === 0) continue;
      selectedList.push({ specialty_id: specialtyId, provider_ids: Array.from(providerIds) });
    }
    let aiResourcesError: AiErrorInfo | undefined;
    try {
      const { data } = await apiClient.post<{ inserted: number; aiResourcesError?: AiErrorInfo }>(
        '/find-providers/care-plan',
        {
          session_id: sessionId,
          selected_providers: selectedList,
        },
      );
      aiResourcesError = data?.aiResourcesError;
    } catch {
      // proceed even on error
    } finally {
      setIsBuildingPlan(false);
    }
    navigate({ pathname: '/redesign-chatbot/plan' }, { state: { sessionId, aiResourcesError } });
  };

  const goToNextOrBuild = async (selections: Map<string, Set<string>>) => {
    // Guard against advancing while the next specialty is still loading — the
    // buttons that call this are disabled in that state, but this protects
    // against prematurely building the care plan with specialties missing.
    if (isWaitingForNext) return;
    if (currentIndex < visibleCategories.length - 1) {
      navigate(
        { pathname: `/redesign-chatbot/matching/${currentIndex + 1}` },
        { state: { sessionId } },
      );
    } else {
      await buildCarePlanWithSelections(selections);
    }
  };

  const handleContinue = () => goToNextOrBuild(selectedProviders);

  const handleSkip = () => goToNextOrBuild(selectedProviders);

  const handleUseTopRecommendation = async () => {
    const topProvider = category?.providers[0];
    if (!topProvider) return;
    const updatedSelections = new Map(selectedProviders);
    updatedSelections.set(category.specialty_id, new Set([topProvider.id]));
    setSelectedProviders(updatedSelections);
    await goToNextOrBuild(updatedSelections);
  };

  if (!isReady || isLoadingCategories || (!category && !isAllDone)) {
    return (
      <div className="min-h-screen bg-background">
        <JourneyProgress currentStep="matching" sessionId={sessionId} />
        <ProviderMatchingSkeleton />
      </div>
    );
  }

  if (!category) {
    return <Navigate to="/redesign-chatbot/transition" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <JourneyProgress currentStep="matching" sessionId={sessionId} />

      <div className="px-3 py-3">
        <div className="">

          {/* ONE large white container — NO overflow-hidden so sticky works */}
          <div className="flex min-h-[600px] items-start rounded-2xl border border-gray-200 bg-white shadow-sm">

            {/* ── LEFT SIDEBAR ──────────────────────────────────────────────── */}
            <aside
              className="hidden w-[300px] flex-shrink-0 self-start rounded-l-2xl border-r border-gray-100 bg-white lg:flex lg:flex-col"
              style={{ position: 'sticky', top: STICKY_OFFSET, maxHeight: `calc(100vh - ${STICKY_OFFSET}px)` }}
            >
              {/* Progress header */}
              <div className="border-b border-gray-100 px-5 py-4">
                <div className="mb-2 flex items-center justify-between gap-2 whitespace-nowrap">
                  <span className="text-xs text-gray-500">
                    Provider Type {currentIndex + 1} of {totalProviderTypes}
                  </span>
                  <span className="text-xs font-medium text-[#026857]">
                    {Math.round(progress)}% Complete
                  </span>
                </div>
                <Progress value={progress} className="h-1.5 bg-gray-200 [&>div]:bg-[#026857]" />
              </div>

              {/* Provider type nav */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <nav className="space-y-1">
                  {(specialtyTypes.length > 0 ? specialtyTypes : visibleCategories).map((item) => {
                    const title = 'title' in item ? item.title : item.name;
                    const loadedIdx = visibleCategories.findIndex(
                      (c) => c.specialty_id === item.specialty_id,
                    );
                    const isLoaded = loadedIdx >= 0;
                    const isActiveItem =
                      visibleCategories[currentIndex]?.specialty_id === item.specialty_id;
                    const selectedSet = selectedProviders.get(item.specialty_id);
                    const selectedProvider =
                      selectedSet && isLoaded
                        ? visibleCategories[loadedIdx]?.providers.find((p) => selectedSet.has(p.id))
                        : undefined;

                    return (
                      <button
                        key={item.specialty_id}
                        type="button"
                        onClick={() => {
                          if (isLoaded) {
                            navigate(
                              { pathname: `/redesign-chatbot/matching/${loadedIdx}` },
                              { state: { sessionId } },
                            );
                          }
                        }}
                        className={`flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          isActiveItem
                            ? 'bg-[#026857]/5 font-medium text-[#026857]'
                            : isLoaded
                              ? 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                              : 'cursor-default text-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`relative flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                              selectedSet && selectedSet.size > 0
                                ? 'border-[#026857] bg-[#026857]'
                                : isActiveItem
                                  ? 'border-[#026857] bg-white shadow-[0_0_0_3px_rgba(2,104,87,0.15)]'
                                  : 'border-gray-300 bg-white'
                            }`}
                          >
                            {isActiveItem && !(selectedSet && selectedSet.size > 0) && (
                              <span className="h-1.5 w-1.5 rounded-full bg-[#026857]" />
                            )}
                          </span>
                          <span className="leading-snug">{title}</span>
                        </div>
                        {selectedProvider && (
                          <span className="ml-6 text-xs text-[#026857]">
                            • Selected: {selectedProvider.name}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* What They Do */}
              <div className="border-t border-gray-100 px-4 py-4">
                <div className="rounded-lg bg-amber-50 px-4 py-3">
                  <div className="mb-1.5 flex items-center gap-2">
                    <Lightbulb size={13} className="text-amber-600" />
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                      What They Do
                    </p>
                  </div>
                  <p className="line-clamp-4 text-xs leading-relaxed text-gray-600">
                    {category.whatTheyDo ?? ''}
                  </p>
                </div>
              </div>

              {/* Use Recommendation button */}
              <div className="border-t border-gray-100 px-4 py-4">
                <Button
                  onClick={handleUseTopRecommendation}
                  disabled={!category.providers.length}
                  size="sm"
                  className="w-full bg-[#026857] text-white hover:bg-[#02584a] disabled:opacity-50"
                >
                  Use Recommendation →
                </Button>
              </div>
            </aside>

            {/* ── RIGHT CONTENT ──────────────────────────────────────────────── */}
            <main className="min-w-0 flex-1 px-8 py-8">

              {/* Category header */}
              <div className="mb-6 border-b border-gray-100 pb-6">
                <h1 className="mb-2 text-3xl font-semibold text-gray-900">{category.title}</h1>
                <p className="leading-relaxed text-gray-600">{category.description ?? ''}</p>
              </div>

              {/* Recommendations heading */}
              <div className="mb-5">
                <h2 className="mb-1 text-xl font-semibold text-gray-900">Our Recommendations</h2>
                <p className="text-sm text-gray-500">
                  Based on your location and needs, here are our top matches:
                </p>
              </div>

              {/* Provider cards */}
              {isLoadingCategories && <ProviderMatchingSkeleton />}

              {!isLoadingCategories && (
                <div className="space-y-5">
                  {category.providers.length === 0 && (
                    <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                      <p className="font-medium text-gray-600">
                        No providers found for this specialty in your area.
                      </p>
                      <p className="mt-2 text-sm text-gray-500">
                        You can skip this step and continue.
                      </p>
                    </div>
                  )}

                  {category.providers.map((provider, index) => (
                    <div key={provider.id} className="relative">
                      {/* TOP MATCH badge */}
                      {index === 0 && (
                        <div className="absolute -top-3 left-4 z-[1]">
                          <span className="rounded-full bg-[#026857] px-3 py-1 text-xs font-semibold text-white">
                            TOP MATCH
                          </span>
                        </div>
                      )}

                      <div
                        className={`rounded-xl border transition-all duration-200 ${
                          isSelected(provider.id)
                            ? 'border-[#026857] bg-[#026857]/5 shadow-[0_4px_12px_rgba(2,104,87,0.1)]'
                            : 'border-gray-200 bg-white hover:border-[#026857]/40 hover:shadow-sm'
                        } ${index === 0 ? 'px-5 pb-5 pt-7' : 'p-5'}`}
                      >
                        <div className="flex gap-4">
                          {/* Avatar */}
                          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#026857] to-[#02584a] text-sm font-bold text-white">
                            {provider.name
                              .split(' ')
                              .slice(0, 3)
                              .map((n) => n[0])
                              .join('')}
                          </div>

                          {/* Provider info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <h3 className="text-base font-semibold text-gray-900">
                                  {provider.name}
                                </h3>
                                <span className="mt-1 inline-flex items-center rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-medium text-[#026857]">
                                  {provider.provider_category_name ?? provider.specialty}
                                </span>
                              </div>
                              {/* Rating top-right */}
                              {provider.rating !== null && (
                                <div className="flex flex-shrink-0 items-center gap-1">
                                  <Star size={14} className="fill-yellow-400 text-yellow-400" />
                                  <span className="text-sm font-medium text-gray-700">
                                    {provider.rating.toFixed(1)}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Location only — no availability badge */}
                            {provider.location && (
                              <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                                <MapPin size={13} />
                                <span>{provider.location}</span>
                              </div>
                            )}

                            {/* Bio */}
                            <div className="mt-3">
                              <p
                                className={`text-sm leading-relaxed text-gray-700 ${
                                  expandedBios.has(provider.id) ? '' : 'line-clamp-4'
                                }`}
                              >
                                {provider.bio}
                              </p>
                              <button
                                type="button"
                                className="mt-1 text-xs font-medium text-[#026857] hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedBios((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(provider.id)) {
                                      next.delete(provider.id);
                                    } else {
                                      next.add(provider.id);
                                    }
                                    return next;
                                  });
                                }}
                              >
                                {expandedBios.has(provider.id) ? 'Read less' : 'Read more'}
                              </button>
                            </div>

                            {/* Action buttons */}
                            <div className="mt-4 flex items-center gap-3">
                              <Button
                                variant={isSelected(provider.id) ? 'default' : 'outline'}
                                size="sm"
                                className={`rounded-lg ${
                                  isSelected(provider.id)
                                    ? 'bg-[#026857] text-white hover:bg-[#02584a]'
                                    : 'border-[#026857] text-[#026857] hover:bg-[#026857] hover:text-white'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectProvider(provider.id);
                                }}
                              >
                                {isSelected(provider.id) ? 'Selected' : 'Select'}
                              </Button>
                              <button
                                type="button"
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedProvider(
                                    expandedProvider === provider.id ? null : provider.id,
                                  );
                                }}
                              >
                                {expandedProvider === provider.id ? 'Less Info' : 'More Info'}
                                <ChevronDown
                                  className={`h-3.5 w-3.5 transition-transform ${
                                    expandedProvider === provider.id ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Expanded details */}
                        {expandedProvider === provider.id && (
                          <div className="mt-5 border-t border-gray-100 pt-5">
                            <div className="grid grid-cols-2 gap-8">

                              {/* LEFT: Contact Info → Experience → Areas of Focus */}
                              <div className="space-y-4">
                                {(provider.phone || provider.email) && (
                                  <div>
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                      Contact Information
                                    </p>
                                    <div className="space-y-2">
                                      {provider.phone && (
                                        <div className="flex items-center gap-2 text-sm text-gray-700">
                                          <Phone size={13} className="flex-shrink-0 text-rose-400" />
                                          <a href={`tel:${provider.phone}`} className="hover:text-[#026857]">
                                            {provider.phone}
                                          </a>
                                        </div>
                                      )}
                                      {provider.email && (
                                        <div className="flex items-center gap-2 text-sm text-gray-700">
                                          <Mail size={13} className="flex-shrink-0 text-violet-400" />
                                          <a href={`mailto:${provider.email}`} className="truncate hover:text-[#026857]">
                                            {provider.email}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {provider.experience && (
                                  <div>
                                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                      Experience
                                    </p>
                                    <p className="text-sm text-gray-700">{provider.experience}</p>
                                  </div>
                                )}

                                {provider.specialties && provider.specialties.length > 0 && (
                                  <div>
                                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                      Areas of Focus
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {provider.specialties.map((spec) => (
                                        <span
                                          key={spec}
                                          className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-700"
                                        >
                                          {spec}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* RIGHT: Credentials */}
                              {provider.credentials && provider.credentials.length > 0 && (
                                <div>
                                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    Credentials
                                  </p>
                                  <ul className="space-y-1">
                                    {provider.credentials.map((cred) => (
                                      <li key={cred} className="flex items-start gap-2 text-sm text-gray-700">
                                        <span className="mt-1 flex-shrink-0 text-gray-400">•</span>
                                        <span>{cred}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Continue / Skip buttons */}
              <div className="mt-8 flex gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleContinue}
                        disabled={isBuildingPlan}
                        className={`h-11 flex-1 rounded-xl text-sm font-medium disabled:opacity-50 ${
                          isWaitingForNext
                            ? 'cursor-not-allowed bg-gray-100 text-gray-400 hover:bg-gray-100'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {isBuildingPlan ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Building your plan...
                          </>
                        ) : isLastStep ? (
                          'Build My Care Plan'
                        ) : (
                          'Continue to Next Provider Type'
                        )}
                      </Button>
                    </TooltipTrigger>
                    {isWaitingForNext && (
                      <TooltipContent className="bg-[#026857] text-white">
                         Finding your next recommendations. Please wait a moment...
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleSkip}
                        disabled={isBuildingPlan}
                        variant="outline"
                        className={`h-11 flex-1 rounded-xl border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                          isWaitingForNext ? 'cursor-not-allowed text-gray-400 hover:bg-white' : ''
                        }`}

                      >
                        {isLastStep ? 'Skip & Build My Care Plan' : 'Skip This Provider Type'}
                      </Button>
                    </TooltipTrigger>
                    {isWaitingForNext && (
                      <TooltipContent className="bg-[#026857] text-white">
                         Finding your next recommendations. Please wait a moment...
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
